"use client";

import { Database, GitBranch, Play, Terminal } from "lucide-react";
import { motion } from "motion/react";
import { SlideInLeft, SlideInRight } from "@/components/ui/motion";
import { useIsMobile } from "@/hooks/use-mobile";

const capabilities = [
  { icon: Terminal, label: "Webhooks & HTTP" },
  { icon: GitBranch, label: "Environments" },
  { icon: Play, label: "Replay & Debug" },
  { icon: Database, label: "Export & Backup" },
];

const codeExample = `// Incoming webhook payload
{
  "event": "order.created",
  "data": {
    "order_id": "ord_8x7k2n",
    "customer": "cust_4m9p1r",
    "total": 149.99,
    "items": [...]
  },
  "timestamp": "2024-01-15T10:32:18Z"
}`;

export function DeveloperSection() {
  const isMobile = useIsMobile();

  return (
    <section className="py-20 lg:py-28 section-base relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-warning/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <SlideInLeft>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-warning/10 border border-warning/30 text-warning text-sm font-medium mb-6">
              <Terminal className="w-4 h-4" />
              For power users
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-5">
              For teams that need <span className="gradient-text">control</span>
            </h2>

            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Full API access, webhook flexibility, and the observability tools
              you expect. Build complex integrations while keeping full
              visibility into what's running.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {capabilities.map((cap, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={isMobile ? {} : { scale: 1.02 }}
                  className="flex items-center gap-3 p-3 sm:p-4 rounded-lg glass hover:border-primary/30 transition-all"
                >
                  <cap.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {cap.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </SlideInLeft>

          {/* Code Block */}
          <SlideInRight>
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-warning/20 rounded-2xl blur-2xl opacity-50" />

              <motion.div
                className="relative glass rounded-xl shadow-2xl overflow-hidden"
                whileHover={isMobile ? {} : { scale: 1.01 }}
              >
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-card/50">
                  <div className="w-3 h-3 rounded-full bg-destructive/60" />
                  <div className="w-3 h-3 rounded-full bg-warning/60" />
                  <div className="w-3 h-3 rounded-full bg-success/60" />
                  <span className="ml-3 text-xs font-mono text-muted-foreground">
                    webhook-payload.json
                  </span>
                </div>
                <pre className="p-5 overflow-x-auto">
                  <code className="text-sm font-mono text-foreground leading-relaxed">
                    {codeExample}
                  </code>
                </pre>
              </motion.div>
            </div>
          </SlideInRight>
        </div>
      </div>
    </section>
  );
}
